<script>
import { createBranch, checkAdminEmail } from '@/api/branch'

/**
 * 지점 등록 페이지 (HQ 관리자 전용).
 *
 * 섹션 구성:
 *  1. 지점 기본정보
 *  2. 초기 관리자 정보 (필수)
 *  3. 수수료 정책 (선택)
 *
 * 등록 완료 시 생성된 지점의 상세 페이지로 이동한다.
 */
export default {
  name: 'BranchCreatePage',
  data() {
    return {
      saving: false,
      errorMsg: '',
      /** 초기 관리자 이메일 중복 검사 결과 */
      adminEmailCheckMsg: '',
      adminEmailCheckLoading: false,

      branchTypeOptions: [
        { title: '직영점', value: 'DIRECT' },
        { title: '대리점', value: 'AGENCY' },
        { title: '가맹점', value: 'FRANCHISE' },
      ],
      branchStatusOptions: [
        { title: '운영중', value: 'ACTIVE' },
        { title: '일시중지', value: 'INACTIVE' },
        { title: '폐점', value: 'CLOSED' },
      ],

      /** 지점 기본정보 폼 */
      branchForm: {
        branchName: '',
        branchNo: '',
        branchType: 'DIRECT',
        branchStatus: 'ACTIVE',
        zipcode: '',
        address1: '',
        address2: '',
        locationX: '',
        locationY: '',
      },

      /** 초기 관리자 폼 */
      adminForm: {
        adminName: '',
        adminEmail: '',
        adminPassword: '',
        adminPosition: '',
        adminPhone: '',
      },

      /** 수수료 정책 (필수 등록) */
      feeForm: {
        buyFeeRate: '',
        sellFeeRate: '',
        feeStartDate: '',
        feeEndDate: '',
        alwaysApplyYn: 'Y',
      },
    }
  },
  methods: {
    /** 초기 관리자 이메일(아이디) 중복 검사 */
    async handleCheckAdminEmail() {
      this.adminEmailCheckMsg = ''
      this.errorMsg = ''
      const email = this.adminForm.adminEmail?.trim()
      if (!email) {
        this.adminEmailCheckMsg = '이메일을 입력한 후 중복 확인해 주세요.'
        return
      }
      this.adminEmailCheckLoading = true
      try {
        const res = await checkAdminEmail(email)
        if (res.data.code === 0) {
          this.adminEmailCheckMsg = '사용 가능한 이메일입니다.'
        } else {
          this.adminEmailCheckMsg = res.data.message || '이미 등록된 이메일입니다.'
        }
      } catch (e) {
        this.adminEmailCheckMsg = e?.response?.data?.message || '중복 검사에 실패했습니다.'
      } finally {
        this.adminEmailCheckLoading = false
      }
    },

    /** 다음 우편번호 서비스 팝업 오픈 */
    openAddressSearch() {
      if (!window.daum?.Postcode) {
        this.errorMsg = '주소 검색 서비스를 불러오는 중입니다. 잠시 후 다시 시도해 주세요.'
        return
      }
      new window.daum.Postcode({
        oncomplete: (data) => {
          // 도로명 주소 우선, 없으면 지번 주소
          const address = data.roadAddress || data.autoRoadAddress || data.jibunAddress
          this.branchForm.zipcode = data.zonecode
          this.branchForm.address1 = address
          // 좌표: Daum Postcode v2에서 x=경도, y=위도 반환
          this.branchForm.locationX = data.x || ''
          this.branchForm.locationY = data.y || ''
        },
      }).open()
    },

    /** 유효성 검사 */
    validate() {
      if (!this.branchForm.branchName?.trim()) {
        this.errorMsg = '지점명은 필수입니다.'
        return false
      }
      if (!this.adminForm.adminName?.trim()) {
        this.errorMsg = '관리자 이름은 필수입니다.'
        return false
      }
      if (!this.adminForm.adminEmail?.trim()) {
        this.errorMsg = '관리자 이메일은 필수입니다.'
        return false
      }
      if (!this.adminForm.adminPassword?.trim()) {
        this.errorMsg = '관리자 초기 비밀번호는 필수입니다.'
        return false
      }
      if (!this.feeForm.buyFeeRate) {
        this.errorMsg = '매수 수수료율은 필수입니다.'
        return false
      }
      if (!this.feeForm.feeStartDate) {
        this.errorMsg = '수수료 정책 적용 시작일은 필수입니다.'
        return false
      }
      if (this.feeForm.alwaysApplyYn !== 'Y' && !this.feeForm.feeEndDate) {
        this.errorMsg = '상시 적용이 아닌 경우 수수료 정책 적용 종료일은 필수입니다.'
        return false
      }
      if (
        this.feeForm.alwaysApplyYn !== 'Y' &&
        this.feeForm.feeStartDate &&
        this.feeForm.feeEndDate &&
        this.feeForm.feeStartDate >= this.feeForm.feeEndDate
      ) {
        this.errorMsg = '수수료 정책 적용 종료일은 시작일보다 이후여야 합니다.'
        return false
      }
      return true
    },

    /** 등록 처리 */
    async handleSave() {
      this.errorMsg = ''
      if (!this.validate()) return

      this.saving = true
      try {
        const payload = {
          ...this.branchForm,
          branchNo: this.branchForm.branchNo.replace(/-/g, ''),
          ...this.adminForm,
          buyFeeRate: parseFloat(this.feeForm.buyFeeRate),
          sellFeeRate: parseFloat(this.feeForm.sellFeeRate) || 0,
          feeStartDate: this.toStartDateTime(this.feeForm.feeStartDate),
          feeEndDate: this.feeForm.alwaysApplyYn === 'Y' ? null : this.toEndDateTime(this.feeForm.feeEndDate),
        }
        const res = await createBranch(payload)
        if (res.data.code !== 0) {
          this.errorMsg = res.data.message || '등록에 실패했습니다.'
          return
        }
        // 등록된 지점의 상세 페이지로 이동
        const branchCode = res.data.branchCode || res.data.data?.branchCode
        this.$router.push(branchCode ? `/branches/${branchCode}` : '/branches')
      } catch (e) {
        this.errorMsg = e?.response?.data?.message || '서버 오류가 발생했습니다.'
      } finally {
        this.saving = false
      }
    },

    handleCancel() {
      this.$router.push('/branches')
    },

    /** 지점구분 레이블 */
    getBranchTypeLabel(type) {
      const map = { DIRECT: '직영점', AGENCY: '대리점', FRANCHISE: '가맹점' }
      return map[type] || type
    },

    /** 사업자번호 000-00-00000 형식으로 포맷 (숫자만 추출 후 적용) */
    formatBranchNo(value) {
      const digits = (value || '').replace(/\D/g, '').slice(0, 10)
      if (digits.length <= 3) return digits
      if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
    },

    toStartDateTime(date) {
      return date ? `${date}T00:00:00` : null
    },

    toEndDateTime(date) {
      return date ? `${date}T23:59:59` : null
    },
  },
}
</script>

<template>
  <div class="branch-create-page">
    <!-- 헤더 -->
    <div class="page-hero mb-7">
      <div class="d-flex align-center ga-1 text-caption text-medium-emphasis mb-4">
        <span class="breadcrumb-link text-primary" @click="$router.push('/branches')">지점관리</span>
        <v-icon size="12">mdi-chevron-right</v-icon>
        <span>신규 등록</span>
      </div>
      <div class="d-flex align-center justify-space-between">
        <div>
          <h1 class="text-h5 font-weight-bold">신규 지점 등록</h1>
          <p class="text-body-2 text-medium-emphasis mt-1">
            지점 정보, 초기 관리자, 수수료 정책을 설정하여 새 지점을 등록합니다.
          </p>
        </div>
      </div>
    </div>

    <!-- 오류 메시지 -->
    <v-alert
      v-if="errorMsg"
      type="error"
      variant="tonal"
      rounded="lg"
      density="compact"
      class="mb-5"
      closable
      @click:close="errorMsg = ''"
    >
      {{ errorMsg }}
    </v-alert>

    <v-row class="branch-layout">
      <!-- 왼쪽: 폼 영역 -->
      <v-col cols="12" xl="8" lg="8" class="form-column">

        <!-- 섹션 1: 지점 기본정보 -->
        <v-card rounded="lg" elevation="0" border class="form-section-card">
          <div class="section-header d-flex align-center ga-3">
            <div class="section-icon-wrap">
              <v-icon size="18" color="primary">mdi-store-outline</v-icon>
            </div>
            <div>
              <div class="text-subtitle-2 font-weight-bold">지점 기본정보</div>
              <div class="text-caption text-medium-emphasis">지점의 기본 정보를 입력합니다.</div>
            </div>
          </div>
          <v-divider />
          <v-card-text class="section-body">
            <v-row class="form-grid">
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="branchForm.branchName"
                  label="지점명 *"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                  placeholder="예: 강남점"
                />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  :model-value="branchForm.branchNo"
                  label="사업자번호"
                  variant="outlined"
                  density="compact"
                  hide-details
                  placeholder="000-00-00000"
                  @update:model-value="branchForm.branchNo = formatBranchNo($event)"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="branchForm.branchType"
                  :items="branchTypeOptions"
                  item-title="title"
                  item-value="value"
                  label="지점구분 *"
                  variant="outlined"
                  density="compact"
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-select
                  v-model="branchForm.branchStatus"
                  :items="branchStatusOptions"
                  item-title="title"
                  item-value="value"
                  label="운영상태 *"
                  variant="outlined"
                  density="compact"
                  hide-details
                />
              </v-col>
              <!-- 주소 찾기 -->
              <v-col cols="12">
                <div class="address-search-row">
                  <v-text-field
                    v-model="branchForm.zipcode"
                    label="우편번호"
                    variant="outlined"
                    density="compact"
                    hide-details
                    readonly
                    placeholder="주소 찾기 버튼을 눌러주세요"
                    class="zipcode-field"
                  />
                  <v-btn
                    color="primary"
                    variant="tonal"
                    rounded="lg"
                    prepend-icon="mdi-map-search-outline"
                    @click="openAddressSearch"
                  >
                    주소 찾기
                  </v-btn>
                </div>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="branchForm.address1"
                  label="주소 (도로명)"
                  variant="outlined"
                  density="compact"
                  hide-details
                  readonly
                  placeholder="위 주소 찾기 버튼을 이용해주세요"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="branchForm.address2"
                  label="상세 주소"
                  variant="outlined"
                  density="compact"
                  hide-details
                  placeholder="층, 호수 등 상세 주소 입력"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="branchForm.locationX"
                  label="경도 (X)"
                  variant="outlined"
                  density="compact"
                  hide-details
                  readonly
                  placeholder="주소 선택 시 자동 입력"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="branchForm.locationY"
                  label="위도 (Y)"
                  variant="outlined"
                  density="compact"
                  hide-details
                  readonly
                  placeholder="주소 선택 시 자동 입력"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- 섹션 2: 초기 관리자 -->
        <v-card rounded="lg" elevation="0" border class="form-section-card">
          <div class="section-header d-flex align-center ga-3">
            <div class="section-icon-wrap">
              <v-icon size="18" color="indigo">mdi-account-tie-outline</v-icon>
            </div>
            <div>
              <div class="text-subtitle-2 font-weight-bold">초기 관리자 정보</div>
              <div class="text-caption text-medium-emphasis">지점 생성 시 등록될 관리자 계정을 입력합니다.</div>
            </div>
            <v-spacer />
            <v-chip size="x-small" color="error" variant="tonal" label>필수</v-chip>
          </div>
          <v-divider />
          <v-card-text class="section-body">
            <v-row class="form-grid">
              <v-col cols="12" sm="7">
                <v-text-field
                  v-model="adminForm.adminName"
                  label="관리자 이름 *"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12" sm="5">
                <v-text-field
                  v-model="adminForm.adminPosition"
                  label="직책"
                  variant="outlined"
                  density="compact"
                  hide-details
                  placeholder="예: 점장"
                />
              </v-col>
              <v-col cols="12">
                <div class="email-check-row">
                  <v-text-field
                    v-model="adminForm.adminEmail"
                    label="이메일 (로그인 ID) *"
                    variant="outlined"
                    density="compact"
                    hide-details="auto"
                    type="email"
                    class="flex-grow-1"
                    @blur="adminEmailCheckMsg = ''"
                  />
                  <v-btn
                    variant="tonal"
                    color="indigo"
                    size="small"
                    :loading="adminEmailCheckLoading"
                    :disabled="!adminForm.adminEmail?.trim()"
                    @click="handleCheckAdminEmail"
                  >
                    중복 확인
                  </v-btn>
                </div>
                <div v-if="adminEmailCheckMsg" class="text-caption mt-1" :class="adminEmailCheckMsg.includes('사용 가능') ? 'text-success' : 'text-error'">
                  {{ adminEmailCheckMsg }}
                </div>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="adminForm.adminPassword"
                  label="초기 비밀번호 *"
                  type="password"
                  variant="outlined"
                  density="compact"
                  hide-details="auto"
                />
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="adminForm.adminPhone"
                  label="전화번호"
                  variant="outlined"
                  density="compact"
                  hide-details
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- 섹션 3: 수수료 정책 (필수) -->
        <v-card rounded="lg" elevation="0" border class="form-section-card">
          <div class="section-header d-flex align-center ga-3">
            <div class="section-icon-wrap">
              <v-icon size="18" color="teal">mdi-percent-outline</v-icon>
            </div>
            <div>
              <div class="text-subtitle-2 font-weight-bold">수수료 정책</div>
              <div class="text-caption text-medium-emphasis">지점의 매수·매도 수수료율을 설정합니다.</div>
            </div>
            <v-spacer />
            <v-chip size="x-small" color="error" variant="tonal" label>필수</v-chip>
          </div>
          <v-divider />
          <v-card-text class="section-body">
            <v-row class="form-grid">
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="feeForm.buyFeeRate"
                  label="매수 수수료율 *"
                  variant="outlined"
                  density="compact"
                  type="number"
                  step="0.000001"
                  hide-details="auto"
                  hint="예: 0.003000 = 0.3%"
                  persistent-hint
                  placeholder="0.003000"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="feeForm.sellFeeRate"
                  label="매도 수수료율"
                  variant="outlined"
                  density="compact"
                  type="number"
                  step="0.000001"
                  hide-details="auto"
                  hint="예: 0.005000 = 0.5%"
                  persistent-hint
                  placeholder="0.005000"
                />
              </v-col>
              <v-col cols="12">
                <v-switch
                  v-model="feeForm.alwaysApplyYn"
                  color="primary"
                  true-value="Y"
                  false-value="N"
                  inset
                  hide-details
                  label="상시 적용"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="feeForm.feeStartDate"
                  label="적용 시작일 *"
                  variant="outlined"
                  density="compact"
                  type="date"
                  hide-details
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="feeForm.feeEndDate"
                  label="적용 종료일"
                  variant="outlined"
                  density="compact"
                  type="date"
                  hide-details
                  :disabled="feeForm.alwaysApplyYn === 'Y'"
                  :placeholder="feeForm.alwaysApplyYn === 'Y' ? '상시 적용' : ''"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

      </v-col>

      <!-- 오른쪽: 요약 패널 -->
      <v-col cols="12" xl="4" lg="4" class="summary-column">
        <div class="summary-sticky">
          <v-card rounded="lg" elevation="0" border class="summary-card">
            <div class="summary-header">
              <div class="text-subtitle-2 font-weight-bold mb-1">등록 요약</div>
            </div>
            <v-divider />
            <v-list density="compact" class="summary-list">
              <v-list-item class="rounded-lg">
                <template #prepend>
                  <v-icon size="16" color="medium-emphasis" class="mr-2">mdi-store-outline</v-icon>
                </template>
                <v-list-item-title class="text-caption text-medium-emphasis">지점명</v-list-item-title>
                <v-list-item-subtitle class="text-body-2 font-weight-medium summary-value">
                  {{ branchForm.branchName || '—' }}
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item class="rounded-lg">
                <template #prepend>
                  <v-icon size="16" color="medium-emphasis" class="mr-2">mdi-tag-outline</v-icon>
                </template>
                <v-list-item-title class="text-caption text-medium-emphasis">지점구분</v-list-item-title>
                <v-list-item-subtitle class="text-body-2 font-weight-medium summary-value">
                  {{ getBranchTypeLabel(branchForm.branchType) }}
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item class="rounded-lg">
                <template #prepend>
                  <v-icon size="16" color="medium-emphasis" class="mr-2">mdi-account-outline</v-icon>
                </template>
                <v-list-item-title class="text-caption text-medium-emphasis">관리자</v-list-item-title>
                <v-list-item-subtitle class="text-body-2 font-weight-medium summary-value">
                  {{ adminForm.adminName || '—' }}
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item class="rounded-lg">
                <template #prepend>
                  <v-icon size="16" color="medium-emphasis" class="mr-2">mdi-percent</v-icon>
                </template>
                <v-list-item-title class="text-caption text-medium-emphasis">매수 수수료율</v-list-item-title>
                <v-list-item-subtitle class="text-body-2 font-weight-medium summary-value">
                  {{ feeForm.buyFeeRate ? ((parseFloat(feeForm.buyFeeRate) * 100).toFixed(4) + '%') : '—' }}
                </v-list-item-subtitle>
              </v-list-item>
              <v-list-item class="rounded-lg">
                <template #prepend>
                  <v-icon size="16" color="medium-emphasis" class="mr-2">mdi-calendar-sync-outline</v-icon>
                </template>
                <v-list-item-title class="text-caption text-medium-emphasis">적용 방식</v-list-item-title>
                <v-list-item-subtitle class="text-body-2 font-weight-medium summary-value">
                  {{ feeForm.alwaysApplyYn === 'Y' ? '상시 적용' : '기간 적용' }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
            <v-divider />
            <div class="summary-actions">
              <v-btn
                color="primary"
                variant="flat"
                block
                rounded="lg"
                size="large"
                :loading="saving"
                @click="handleSave"
              >
                <v-icon start>mdi-check</v-icon>
                지점 등록
              </v-btn>
              <v-btn
                variant="outlined"
                block
                rounded="lg"
                :disabled="saving"
                @click="handleCancel"
              >
                취소
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<style scoped>
.branch-create-page {
  max-width: 1440px;
  margin: 0 auto;
  padding-bottom: 48px;
}

.page-hero {
  padding-top: 4px;
}

.branch-layout {
  --v-gutter-x: 32px;
  --v-gutter-y: 28px;
  align-items: flex-start;
}

.form-column {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.form-section-card {
  overflow: hidden;
}

/* ── 브레드크럼 ── */
.breadcrumb-link {
  cursor: pointer;
}
.breadcrumb-link:hover {
  text-decoration: underline;
}

.section-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: rgba(var(--v-theme-primary), 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.section-header {
  min-height: 76px;
  padding: 20px 28px;
  background: rgba(255, 255, 255, 0.035);
}

.section-body {
  padding: 28px;
}

.form-grid {
  --v-gutter-x: 18px;
  --v-gutter-y: 22px;
}

.address-search-row,
.email-check-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.zipcode-field {
  max-width: 180px;
  flex: 0 0 180px;
}

.address-search-row .v-btn,
.email-check-row .v-btn {
  min-height: 40px;
}

.summary-column {
  min-width: 0;
}

.summary-sticky {
  position: sticky;
  top: 84px;
}

.summary-card {
  overflow: hidden;
}

.summary-header {
  min-height: 64px;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.035);
}

.summary-list {
  padding: 14px;
}

.summary-list :deep(.v-list-item) {
  min-height: 64px;
  padding-inline: 12px;
}

.summary-value {
  color: rgba(var(--v-theme-on-surface), 0.92) !important;
  opacity: 1;
}

.summary-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
}

.branch-create-page :deep(.v-field) {
  border-radius: 10px;
}

.branch-create-page :deep(.v-input--density-compact .v-field) {
  min-height: 44px;
}

@media (max-width: 1279px) {
  .branch-layout {
    --v-gutter-x: 0;
  }

  .summary-sticky {
    position: static;
  }
}

@media (max-width: 599px) {
  .branch-create-page {
    padding-bottom: 32px;
  }

  .section-header,
  .section-body {
    padding-inline: 18px;
  }

  .address-search-row,
  .email-check-row {
    flex-direction: column;
  }

  .zipcode-field,
  .address-search-row .v-btn,
  .email-check-row .v-btn {
    width: 100%;
    max-width: none;
    flex-basis: auto;
  }
}
</style>
